<template>
    <div v-if="user" class="gap-xl col-span-12 grid">
        <div v-if="editing" class="col-span-7">
            <AssociationComp
                v-if="editing"
                :type="'location'"
                :item-pool="searchPool as UUIDLocation[]"
                :associated-items="userLocations as UUIDLocation[]"
                @save="handleLocationEdit"
                @cancel="stopEditing"
            />
        </div>
        <div v-else class="gap-md col-span-7 flex flex-col">
            <div class="gap-sm flex">
                <button
                    class="flex cursor-pointer p-0"
                    aria-label="Go back to the previous page"
                    style="padding-top: 2px"
                    @click="router.back()"
                >
                    <i class="i-arrow-left h-6 w-6" />
                </button>
                <div class="gap-sm flex flex-col">
                    <h1>
                        {{ user.FIRST_NAME && user.LAST_NAME ? user.FIRST_NAME + ' ' + user.LAST_NAME : user.USERNAME }}
                    </h1>
                    <p>All changes made on a user's page will be reflected in all areas where they are involved.</p>
                </div>
            </div>
            <OverviewTabs
                :tabs="tabs"
                @add="setupAssociationModal"
                @remove="handleRemove"
                @tab-change="handleTabChange"
            />
        </div>
        <section name="user Info" class="gap-md col-span-4 col-start-9 flex flex-col">
            <div data-testid="user-info-header" class="gap-sm flex items-center">
                <h2>User info</h2>
                <div class="gap-xs flex items-center">
                    <button
                        aria-label="Edit user details"
                        class="bg-primary p-xs rounded-md hover:bg-(--surface-highlight)"
                        type="button"
                        @click="openEditUserDetailsModal"
                    >
                        <i class="i-edit block" />
                    </button>
                    <button
                        aria-label="Open account actions"
                        class="bg-primary p-xs rounded-md hover:bg-(--surface-highlight)"
                        type="button"
                        @click="openAccountActionsModal"
                    >
                        <i class="i-vertical-dots block" />
                    </button>
                </div>
            </div>
            <div class="gap-xs flex flex-col">
                <AvatarArea
                    :first-name="user.FIRST_NAME ?? user.USERNAME"
                    :last-name="user.LAST_NAME"
                    :editable="false"
                    :size="'large'"
                />
                <p class="bold">Name:</p>
                <p>
                    {{ user.FIRST_NAME && user.LAST_NAME ? user.FIRST_NAME + ' ' + user.LAST_NAME : user.USERNAME }}
                </p>
            </div>
            <div class="gap-xs flex flex-col">
                <p class="bold">Email address:</p>
                <a :href="'mailto:' + user.EMAIL" class="text-md underline" @click.stop>{{ user.EMAIL }}</a>
            </div>
            <div class="gap-xs flex flex-col">
                <p class="bold">Phone number:</p>
                <p>{{ user.PHONE_NUMBER || '-' }}</p>
            </div>
            <div class="gap-xs flex flex-col">
                <p class="bold">Account status:</p>
                <TagComponent :value="user.ENABLED ? 'Enabled' : 'Disabled'" :show-dot="true" />
            </div>
        </section>
        <association-modal
            v-if="showAssociationModal"
            :association-id="userId"
            :association-from="associationFrom"
            :association-to="associationTo"
            :search-pool="searchPool"
            @close="closeAssociationModal"
            @add="handleAdd"
        />
        <UserDetailsEditModal
            v-if="showEditUserDetailsModal && user"
            :first-name="user.FIRST_NAME"
            :last-name="user.LAST_NAME"
            :email="user.EMAIL"
            :phone-number="user.PHONE_NUMBER"
            @cancel="closeEditUserDetailsModal"
            @submit="handleUserDetailsSubmit"
        />
        <UserAccountActionsModal
            v-if="showAccountActionsModal && user"
            :user="user"
            @cancel="closeAccountActionsModal"
            @update="setup()"
        />
    </div>
</template>

<script setup lang="ts">
import { ref, onMounted, computed, watch, defineAsyncComponent, markRaw } from 'vue';
import { useOrganizationStore } from '@/stores/organization';
import OverviewTabs from '@/components/overview-tabs.vue';
import AvatarArea from '@/components/avatar-area.vue';
import UserAccountActionsModal from '@/components/manager/user-modal/user-account-actions-modal.vue';
import UserDetailsEditModal from '@/components/manager/user-modal/user-details-edit-modal.vue';
import router from '@/router';
import { useRoute } from 'vue-router';
import {
    handleAssociationAdd,
    handleAssociationRemove,
    isGroup,
    isRole,
    getLowestLevelCheckedItems,
    getAllCheckedItems,
    getAllItemsFromCascade,
    getHighestLevelParent,
} from '@/stores/helpers';
import AssociationComp from '@/components/manager/association-comp.vue';
import TagComponent from '@/components/ui/tag/TagComponent.vue';
import type { User, Tab, Group, Role, UUIDLocation } from '@/stores/store-interfaces';

const associationModal = defineAsyncComponent(() => import('@/components/manager/association-modal.vue'));
const organizationStore = useOrganizationStore();
const usersOrganization = computed(() => organizationStore.usersOrganization);
const user = ref<User>();
const showAssociationModal = ref(false);
const showEditUserDetailsModal = ref(false);
const showAccountActionsModal = ref(false);
const associationFrom = 'users';
const associationTo = ref<string>('');
const tabs = ref<Tab[]>([]);
const searchPool = ref<Array<Group | Role | UUIDLocation>>([]);
const route = useRoute();
const editing = ref(false);

// associations
const userGroups = ref<Group[]>([]);
const userRoles = ref<Role[]>([]);
const userLocations = ref<UUIDLocation[]>([]);
const locationCount = ref<number>(0);

const currentTab = computed(() => route.query.selected as string);

const props = defineProps({
    userId: {
        type: String,
        required: true,
    },
});

function closeAssociationModal() {
    showAssociationModal.value = false;
}

function stopEditing() {
    editing.value = false;
}

function openEditUserDetailsModal() {
    showEditUserDetailsModal.value = true;
}

function closeEditUserDetailsModal() {
    showEditUserDetailsModal.value = false;
}

function openAccountActionsModal() {
    showAccountActionsModal.value = true;
}

function closeAccountActionsModal() {
    showAccountActionsModal.value = false;
}

async function setup() {
    if (currentTab.value === undefined) {
        router.replace({ query: { selected: 'groups' } });
    }
    user.value = await organizationStore.getUser(props.userId);
    getTabData();

    tabs.value = [
        {
            id: 0,
            name: 'groups',
            component: markRaw(defineAsyncComponent(() => import('@/components/manager/overviews/group-overview.vue'))),
            data: computed(() => userGroups.value),
            associationCount: computed(() => user.value?.groups_count),
        },
        {
            id: 1,
            name: 'roles',
            component: markRaw(defineAsyncComponent(() => import('@/components/manager/overviews/role-overview.vue'))),
            data: computed(() => userRoles.value),
            associationCount: computed(() => user.value?.roles_count),
        },
        {
            id: 2,
            name: 'locations',
            component: markRaw(
                defineAsyncComponent(() => import('@/components/manager/overviews/location-overview.vue')),
            ),
            data: computed(() => userLocations.value),
        },
    ];
}

onMounted(async () => {
    if (organizationStore.usersOrganization?.organization_id != undefined) {
        setup();
    }
});

async function handleTabChange(tabId: number) {
    if (tabId == 0 && userGroups.value.length == 0) {
        userGroups.value = await organizationStore.getAssociation('users', props.userId, 'groups');
    } else if (tabId == 1 && userRoles.value.length == 0) {
        userRoles.value = await organizationStore.getAssociation('users', props.userId, 'roles');
    } else if (tabId == 2 && userLocations.value && userLocations.value.length == 0) {
        userLocations.value = await organizationStore.getLocationsByUserId(props.userId);
    }
    router.replace({ query: { selected: tabs.value[tabId].name } });
}

async function handleAdd(data: Group[] | Role[]) {
    await handleAssociationAdd(data, user.value?.ID ?? '', 'users')
        .then(async () => {
            const firstItem = data[0];

            if (firstItem && isGroup(firstItem)) {
                userGroups.value.push(...(data as Group[]));
                user.value!.groups_count += data.length;
            } else if (firstItem && isRole(firstItem)) {
                console.log(data);
                userRoles.value.push(...(data as Role[]));
                user.value!.roles_count += data.length;
            }
            associationTo.value = '';
            closeAssociationModal();
            organizationStore.setToastMessage('Added', 'check');
        })
        .catch(() => {
            organizationStore.setToastMessage('Something went wrong', 'warning');
        });
}

async function handleUserDetailsSubmit(values: {
    first_name: string;
    last_name: string;
    email: string;
    phone_number: string;
}) {
    try {
        const updatedUser = await organizationStore.updateUserDetails(props.userId, {
            firstName: values.first_name,
            lastName: values.last_name,
            email: values.email,
            phoneNumber: values.phone_number === '' ? null : values.phone_number,
        });

        if (user.value) {
            user.value = { ...user.value, ...updatedUser };
        }

        showEditUserDetailsModal.value = false;
        organizationStore.setToastMessage('User details updated', 'check');
    } catch {
        organizationStore.setToastMessage('Failed to update user details', 'warning');
    }
}

async function handleRemove(data: unknown) {
    await handleAssociationRemove(data, user.value?.ID ?? '', 'users')
        .then(async () => {
            if (isGroup(data)) {
                const group = data as Group;
                userGroups.value = userGroups.value.filter(
                    (existingGroup) => existingGroup.organization_group_id !== group.organization_group_id,
                );
                user.value!.groups_count! -= 1;
            } else if (isRole(data)) {
                const role = data as Role;
                userRoles.value = userRoles.value.filter(
                    (existingRole) => existingRole.organization_role_id !== role.organization_role_id,
                );
                user.value!.roles_count! -= 1;
            }

            associationTo.value = '';
            closeAssociationModal();
            organizationStore.setToastMessage('Removed', 'check');
        })
        .catch(() => {
            organizationStore.setToastMessage('Something went wrong', 'warning');
        });
}

async function setupAssociationModal(tabId: number) {
    if (tabId === 0) {
        const groups = await organizationStore.getGroups();
        const validGroups = groups.filter(
            (group) =>
                !userGroups.value.some((someGroup) => someGroup.organization_group_id === group.organization_group_id),
        );
        searchPool.value = validGroups;
        associationTo.value = 'groups';
        showAssociationModal.value = true;
    } else if (tabId === 1) {
        const roles = await organizationStore.getRoles();
        const validRoles = roles.filter(
            (role) => !userRoles.value.some((userRole) => userRole.organization_role_id === role.organization_role_id),
        );
        searchPool.value = validRoles;
        associationTo.value = 'roles';
        showAssociationModal.value = true;
    } else if (tabId === 2) {
        searchPool.value = await organizationStore.getLocations();
        editing.value = true;
    }
}

async function getTabData() {
    locationCount.value =
        (user.value?.regions_count ?? 0) +
        (user.value?.sites_count ?? 0) +
        (user.value?.buildings_count ?? 0) +
        (user.value?.rooms_count ?? 0) +
        (user.value?.pods_count ?? 0);
    if (currentTab.value === 'groups' && userGroups.value.length == 0) {
        userGroups.value = await organizationStore.getAssociation('users', props.userId, 'groups');
    } else if (currentTab.value === 'roles' && userRoles.value.length == 0) {
        userRoles.value = await organizationStore.getAssociation('users', props.userId, 'roles');
    } else if (currentTab.value === 'locations') {
        userLocations.value = await organizationStore.getLocationsByUserId(props.userId);
    }
}

async function handleLocationEdit(checkedItems: Set<string>) {
    const locationSearchPool = searchPool.value as UUIDLocation[];

    const newLowestLevelItems = getLowestLevelCheckedItems(locationSearchPool, checkedItems);

    const oldLowestLevelItems = getLowestLevelCheckedItems(userLocations.value, checkedItems);

    const itemsToAdd = newLowestLevelItems.filter(
        (newItem) => !oldLowestLevelItems.some((oldItem) => oldItem === newItem),
    );

    // Handle additions
    // TODO remove the for loop and make a bulk API call instead
    if (itemsToAdd.length > 0) {
        const payload = itemsToAdd.map((item) => ({
            locationId: item,
        }));

        await organizationStore.associateLocation('users', props.userId, 'locations', payload).then(() => {
            organizationStore.setToastMessage('Locations successfully added', 'check');
        });
    }
    // // Handle deletions

    const allNewItems = getAllCheckedItems(locationSearchPool, checkedItems);

    const allOldItems = getAllItemsFromCascade(userLocations.value);

    const itemsToRemove = allOldItems.filter((oldItem) => !allNewItems.some((newItem) => newItem.id === oldItem.id));

    if (itemsToRemove.length > 0) {
        const parentsToDelete = getHighestLevelParent(itemsToRemove);
        const payload = parentsToDelete.map((item) => ({
            locationId: item.id,
        }));

        await organizationStore.removeLocationAssociation('users', props.userId, 'locations', payload).then(() => {
            organizationStore.setToastMessage('Locations successfully removed', 'check');
        });
    }
    editing.value = false;
}

watch(usersOrganization, async () => {
    setup();
});

watch(currentTab, async () => {
    getTabData();
});

watch(editing, () => {
    if (!editing.value) {
        getTabData();
    }
});
</script>
